/**
 * Public identifiers as the administration area edits them (TASK-0060).
 *
 * Two address shapes are encoded here rather than left to the screens:
 *
 * - **a rename names the account**, `POST /admin/users/{id}/public-identifier`, because the account is the resource being
 *   edited — the handle is its attribute, not an entity of its own;
 * - **the blacklist is an exact set**, so its own routes are keyed by entry identifier and the list is paged. The match
 *   it applies is exact and case-insensitive: `CEO` and `ceo` are one entry, and `ceo-office` is a different handle that
 *   the reservation does not touch.
 *
 * The account lookup goes through the handle an administrator was given over a support conversation, which is the reason
 * the handle exists at all.
 */

import { apiClient } from '@/lib/apiClient';

/** One account as the administration surface reads it (`AdminUserDto`). */
export interface AdminUserAccount {
  /** Account identifier. */
  userId: string;
  /** Sign-in address; an administrator may see it. */
  email: string;
  /** Display name. */
  displayName: string;
  /** Public handle, or `null` when the account has not registered one. */
  publicIdentifier: string | null;
  /** Whether the account is disabled. */
  isBanned: boolean;
  /** Registration instant. */
  createdAtUtc: string;
}

/** One reserved handle (`BlacklistedPublicIdentifierDto`). */
export interface BlacklistedPublicIdentifier {
  /** Entry identifier, used to release the reservation. */
  id: string;
  /** The reserved value as the administrator typed it. */
  identifier: string;
  /** The administrator's reason, or `null`. */
  note: string | null;
  /** Administrator who added the entry. */
  createdByUserId: string;
  /** Instant the entry was added. */
  createdAtUtc: string;
}

/** One page of reserved handles; the envelope `PagedResult<T>` uses. */
export interface BlacklistPage {
  items: BlacklistedPublicIdentifier[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** Administrative public-identifier operations. */
export const adminPublicIdentifierApi = {
  /** Finds the account that owns a handle; `404` when no account does. */
  async findUser(publicIdentifier: string): Promise<AdminUserAccount> {
    // The lookup answers the account detail (TASK-0061): it moved with the account routes to `/admin/users`, and it answers
    // the same object the detail page shows rather than a page of one row.
    const { data } = await apiClient.get<AdminUserAccount>('/admin/users/by-public-identifier', {
      params: { publicIdentifier },
    });

    return data;
  },

  /** Renames the handle of one account. */
  async changeUserIdentifier(userId: string, publicIdentifier: string): Promise<AdminUserAccount> {
    const { data } = await apiClient.post<AdminUserAccount>(
      `/admin/users/${userId}/public-identifier`,
      { publicIdentifier },
    );

    return data;
  },

  /** Reads one page of the blacklist, newest first. */
  async listBlacklist(page = 1, pageSize = 20): Promise<BlacklistPage> {
    const { data } = await apiClient.get<BlacklistPage>('/admin/public-identifiers/blacklist', {
      params: { page, pageSize },
    });

    return data;
  },

  /** Reserves a handle so that no account may take it. */
  async blacklist(
    publicIdentifier: string,
    note?: string | null,
  ): Promise<BlacklistedPublicIdentifier> {
    const { data } = await apiClient.post<BlacklistedPublicIdentifier>(
      '/admin/public-identifiers/blacklist',
      { publicIdentifier, note: note ?? null },
    );

    return data;
  },

  /** Releases a reservation. */
  async release(entryId: string): Promise<BlacklistedPublicIdentifier> {
    const { data } = await apiClient.delete<BlacklistedPublicIdentifier>(
      `/admin/public-identifiers/blacklist/${entryId}`,
    );

    return data;
  },
};
